import './teacher-drive-time-bubble.css'
import React from 'react'
import { HomeIcon } from '@heroicons/react/20/solid'
import { BsCarFrontFill } from 'react-icons/bs'
import { MdCheck, MdClose, MdDoDisturb, MdMailOutline, MdSchool } from 'react-icons/md'
import { createEllipses } from '../../../utilityFunctions'

const resolveTeacherImage = (rawImage) => {
    const image = String(rawImage || '').trim()
    if (!image) return ''
    return image.startsWith('http') ? image : `https://app.atharo.app/images/${image}`
}

const getInstrumentLabels = (suggestionObject) => {
    const instruments = suggestionObject?.instrumentList || suggestionObject?.InstrumentList || []
    return (Array.isArray(instruments) ? instruments : [])
        .map((instrument) => {
            if (typeof instrument === 'string') return instrument.trim()
            return String(instrument?.parentInstrument || instrument?.name || '').trim()
        })
        .filter(Boolean)
        .filter((instrument, index, list) => list.indexOf(instrument) === index)
}

const CalendarEventInfo = ({ suggestion }) => (
    <div className='temp-teacher-distance-info__copy'>
        <div className='temp-teacher-distance-info'>
            <div className='temp-teacher-distance-info__item temp-teacher-distance__teacher-name'>
                {createEllipses(suggestion.eventOrTeacherObject.name, 19)}
            </div>
        </div>
        {suggestion.distance === '1 ft' ? (
            <div className='temp-teacher-distance-info'>
                <MdSchool size={12} fill='#94A3B8' />
                <div className='temp-teacher-distance-info__item temp-teacher-distance-info__distance-time'>
                    at house {suggestion.eventOrTeacherObject.day} {suggestion.eventOrTeacherObject.time}
                </div>
            </div>
        ) : (
            <div className='temp-teacher-distance-info'>
                <BsCarFrontFill size={12} fill='#94A3B8' />
                <div className='temp-teacher-distance-info__item temp-teacher-distance-info__distance-time'>
                    {suggestion.duration} | {suggestion.eventOrTeacherObject.day} {suggestion.eventOrTeacherObject.time}
                </div>
            </div>
        )}
    </div>
)

const HomeLocationInfo = ({ suggestion }) => (
    <div className='temp-teacher-distance-info__copy'>
        <div className='temp-teacher-distance-info'>
            <div className='temp-teacher-distance-info__item temp-teacher-distance__teacher-name'>
                {createEllipses(suggestion.eventOrTeacherObject.name, 19)}
            </div>
        </div>
        <div className='temp-teacher-distance-info'>
            <HomeIcon width={12} fill='#94A3B8' />
            <div className='temp-teacher-distance-info__item temp-teacher-distance-info__distance-time'>
                {suggestion.duration}
            </div>
        </div>
    </div>
)

const InvitationStatus = ({ invitations, suggestion }) => {
    const teacherName = suggestion.eventOrTeacherObject.name
    const invitation = invitations.find(
        (item) => item.teacherName === teacherName
    )
    if (!invitation) return null

    if (invitation.interested) {
        return (
            <span
                className='temp-teacher-distance-info__invitation-indicator is-interested'
                aria-label={`${teacherName} accepted the invitation`}
                title='Accepted invitation'
            >
                <MdCheck color='#fff' aria-hidden='true' />
            </span>
        )
    }
    if (!invitation.dateResponded) {
        return (
            <span
                className='temp-teacher-distance-info__invitation-indicator is-pending'
                aria-label={`${teacherName} has not responded to the invitation`}
                title='Awaiting response'
            >
                <MdMailOutline color='#fff' aria-hidden='true' />
            </span>
        )
    }
    return (
        <span
            className='temp-teacher-distance-info__invitation-indicator is-declined'
            aria-label={`${teacherName} declined the invitation`}
            title='Declined invitation'
        >
            <MdClose color='#fff' aria-hidden='true' />
        </span>
    )
}

const TeacherDriveTimeBubble = ({
    suggestion,
    invitations = [],
    setSchedulingHelperTeacherId = () => {},
    accentColor,
    showInstruments = false,
    onSelect,
    onHoverStart,
    onHoverEnd,
    isHighlighted = false,
    isSelected = false,
    showInvitationStatus = true,
}) => {
    const suggestionObject = suggestion?.eventOrTeacherObject || {}
    const teacherId = suggestionObject.teacherId || suggestionObject.id
    const imageUrl = resolveTeacherImage(suggestionObject.imageUrl)
    const instrumentLabels = getInstrumentLabels(suggestionObject)
    const hasCapacity = suggestionObject.moreStudents !== false

    const handleSelect = () => {
        setSchedulingHelperTeacherId(teacherId)
        if (onSelect) onSelect(suggestion, teacherId)
    }

    return (
        <button
            type='button'
            className={`temp-teacher-distance-popup-item${accentColor ? ' temp-teacher-distance-popup-item--accented' : ''}${isHighlighted ? ' is-highlighted' : ''}${isSelected ? ' is-selected' : ''}`}
            style={accentColor ? { '--teacher-accent-color': accentColor } : undefined}
            onClick={handleSelect}
            onMouseEnter={() => onHoverStart && onHoverStart(suggestion, teacherId)}
            onMouseLeave={() => onHoverEnd && onHoverEnd(suggestion, teacherId)}
            aria-pressed={isSelected}
        >
            <div className='temp-teacher-distance-info__wrapper'>
                <span className='temp-teacher-distance-teacher-avatar'>
                    {showInvitationStatus && (
                        <InvitationStatus invitations={invitations} suggestion={suggestion} />
                    )}
                    {!hasCapacity && (
                        <span
                            className='temp-teacher-distance-teacher-full'
                            aria-label={`${suggestionObject.name || 'Teacher'} is not accepting students`}
                        >
                            <MdDoDisturb aria-hidden='true' />
                        </span>
                    )}
                    {imageUrl ? (
                        <img
                            className='temp-teacher-distance-teacher-img'
                            style={!hasCapacity ? { opacity: 0.5 } : undefined}
                            src={imageUrl}
                            alt={suggestionObject.name || 'Teacher'}
                        />
                    ) : (
                        <span className='temp-teacher-distance-teacher-img temp-teacher-distance-teacher-img--placeholder'>
                            {String(suggestionObject.name || 'T').charAt(0)}
                        </span>
                    )}
                </span>
                {suggestionObject.type === 'calendarEvent' ? (
                    <CalendarEventInfo suggestion={suggestion} />
                ) : (
                    <HomeLocationInfo suggestion={suggestion} />
                )}
            </div>
            {showInstruments && instrumentLabels.length > 0 && (
                <div className='temp-teacher-distance-info__instruments'>
                    {instrumentLabels.map((instrument) => (
                        <span key={instrument}>{instrument}</span>
                    ))}
                </div>
            )}
        </button>
    )
}

export default TeacherDriveTimeBubble
